import React, { useCallback, useEffect, useMemo, useState } from 'react';
import particularesApiService from '../../services/ParticularesApiService';
import TableExportActions from '../common/TableExportActions';
import EstadoAcademicoChip from './EstadoAcademicoChip';
import {
  ASISTENCIA_MINIMA,
  formatPorcentaje,
  formatPromedio,
  getEstadoAcademico,
  isNotaDesaprobada,
  normalizeEstadoAcademicoCuenta
} from '../../utils/evaluaciones';
import './EstadoAcademico.css';

// Estado académico de la cuenta particular: indicadores y grillas por curso (materia), por alumno y, en
// "Modo colegio", por división. Cada grilla se descarga en Excel o PDF. Lo usan el panel del administrador
// (todos los cursos) y el del preceptor (sus cursos). "onVerCurso" abre la matriz de notas del curso.

const normalizeText = (value) => String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const Kpi = ({ icon, label, value, tone }) => (
  <article className={`ea-kpi ea-kpi-${tone}`}>
    <i className={`fas ${icon}`} aria-hidden="true" />
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  </article>
);

const Reparto = ({ aprobados, enRiesgo, sinNotas }) => {
  const total = aprobados + enRiesgo + sinNotas;
  if (total === 0) return <span className="ea-muted">Sin alumnos</span>;
  const pct = (value) => `${Math.round((value * 100) / total)}%`;
  return (
    <div className="ea-reparto" title={`${aprobados} aprobados · ${enRiesgo} en riesgo · ${sinNotas} sin notas`}>
      <div className="ea-reparto-bar">
        <span className="ok" style={{ width: pct(aprobados) }} />
        <span className="risk" style={{ width: pct(enRiesgo) }} />
        <span className="none" style={{ width: pct(sinNotas) }} />
      </div>
      <small>{aprobados} ✓ · {enRiesgo} en riesgo · {sinNotas} sin notas</small>
    </div>
  );
};

const TIPOS_ALERTA = {
  academica: { label: 'Notas', icon: 'fa-graduation-cap' },
  asistencia: { label: 'Asistencia', icon: 'fa-user-clock' },
  ausencias: { label: 'Ausencias seguidas', icon: 'fa-calendar-times' }
};

const esBajaAsistencia = (asistencia) => asistencia?.total >= 3 && asistencia?.porcentaje !== null && asistencia.porcentaje < ASISTENCIA_MINIMA;

const AsistenciaCelda = ({ asistencia }) => {
  if (!asistencia || asistencia.total === 0) return <span className="ea-muted">Sin tomas</span>;
  return (
    <span className={`ea-asistencia${esBajaAsistencia(asistencia) ? ' low' : ''}`}>
      <b>{formatPorcentaje(asistencia.porcentaje)}</b>
      <small>{asistencia.ausentes} {asistencia.ausentes === 1 ? 'falta' : 'faltas'} · {asistencia.total} tomas</small>
    </span>
  );
};

const EstadoAcademicoPanel = ({ titulo = 'Estado académico', subtitulo = '', cuentaNombre = '', onVerCurso }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('inicial');
  const [search, setSearch] = useState('');
  const [alumnoDetalle, setAlumnoDetalle] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await particularesApiService.getEstadoAcademico();
      const normalizado = normalizeEstadoAcademicoCuenta(response?.academico || response?.Academico || {});
      setData(normalizado);
      // Si hay alertas, se abre en esa pestaña la primera vez.
      setTab((prev) => (prev === 'inicial' ? (normalizado.alertas.length > 0 ? 'alertas' : 'cursos') : prev));
      setError('');
    } catch (err) {
      setError(err?.message || 'No pudimos cargar el estado académico.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tabs = useMemo(() => [
    ...(data?.alertas?.length ? [{ key: 'alertas', label: `Alertas (${data.alertas.length})`, icon: 'fa-exclamation-triangle' }] : []),
    { key: 'cursos', label: 'Cursos', icon: 'fa-layer-group' },
    { key: 'alumnos', label: 'Alumnos', icon: 'fa-user-graduate' },
    ...(data?.modoColegio && data.divisiones.length > 0 ? [{ key: 'divisiones', label: 'Divisiones', icon: 'fa-sitemap' }] : [])
  ], [data]);

  const term = normalizeText(search.trim());
  const filtrar = (rows, getText) => (term ? rows.filter((row) => normalizeText(getText(row)).includes(term)) : rows);
  const cursos = filtrar(data?.cursos || [], (c) => `${c.nombre} ${c.divisionNombre}`);
  const alumnos = filtrar(data?.alumnos || [], (a) => `${a.nombre} ${a.email} ${a.divisiones} ${a.cursos.map((c) => c.nombreCurso).join(' ')}`);
  const divisiones = filtrar(data?.divisiones || [], (d) => d.nombreCompleto);
  const alertas = filtrar(data?.alertas || [], (a) => `${a.alumnoNombre} ${a.divisiones} ${a.mensaje}`);
  const conDivision = Boolean(data?.modoColegio);
  const conAsistencia = Boolean(data?.modoColegio && data?.totales.tieneAsistencias);
  const tabActiva = tab === 'inicial' ? 'cursos' : tab;
  const abrirAlumno = (idAlumno) => setAlumnoDetalle((data?.alumnos || []).find((a) => a.id === idAlumno) || null);
  const archivo = `estado-academico-${tabActiva}${cuentaNombre ? `-${cuentaNombre}` : ''}`;
  const tituloExport = `Estado académico - ${(tabs.find((t) => t.key === tabActiva)?.label || '').replace(/ \(\d+\)$/, '')}${cuentaNombre ? ` - ${cuentaNombre}` : ''}`;

  const exportColumns = {
    cursos: [
      { key: 'nombre', title: 'Curso' },
      ...(conDivision ? [{ key: 'divisionNombre', title: 'División', exportValue: (v) => v || '-' }] : []),
      { key: 'totalAlumnos', title: 'Alumnos' },
      { key: 'totalEvaluaciones', title: 'Evaluaciones' },
      { key: 'promedioGeneral', title: 'Promedio', exportValue: (v) => formatPromedio(v) },
      { key: 'aprobados', title: 'Aprobados' },
      { key: 'enRiesgo', title: 'En riesgo' },
      { key: 'sinNotas', title: 'Sin notas' },
      ...(conAsistencia ? [
        { key: 'porcentajeAsistencia', title: 'Asistencia', exportValue: (v) => formatPorcentaje(v) },
        { key: 'alumnosBajaAsistencia', title: 'Alumnos con baja asistencia' }
      ] : [])
    ],
    alumnos: [
      { key: 'nombre', title: 'Alumno' },
      { key: 'email', title: 'Email' },
      ...(conDivision ? [{ key: 'divisiones', title: 'División', exportValue: (v) => v || '-' }] : []),
      { key: 'cursos', title: 'Cursos', exportValue: (cursosAlumno) => cursosAlumno.map((c) => `${c.nombreCurso}: ${formatPromedio(c.promedio)} (${getEstadoAcademico(c.estado).label})`).join(' | ') },
      { key: 'promedioGeneral', title: 'Promedio general', exportValue: (v) => formatPromedio(v) },
      { key: 'cursosEnRiesgo', title: 'Cursos en riesgo' },
      { key: 'estado', title: 'Estado', exportValue: (v) => getEstadoAcademico(v).label },
      ...(conAsistencia ? [
        { key: 'asistencia', title: 'Asistencia', exportValue: (v) => (v.total ? formatPorcentaje(v.porcentaje) : '-') },
        { key: 'asistencia', title: 'Faltas', exportValue: (v) => v.ausentes },
        { key: 'asistencia', title: 'Tomas', exportValue: (v) => v.total }
      ] : [])
    ],
    divisiones: [
      { key: 'nombreCompleto', title: 'División' },
      { key: 'totalCursos', title: 'Cursos' },
      { key: 'totalAlumnos', title: 'Alumnos' },
      { key: 'promedioGeneral', title: 'Promedio', exportValue: (v) => formatPromedio(v) },
      { key: 'aprobados', title: 'Aprobados' },
      { key: 'enRiesgo', title: 'En riesgo' },
      { key: 'sinNotas', title: 'Sin notas' },
      ...(conAsistencia ? [{ key: 'porcentajeAsistencia', title: 'Asistencia', exportValue: (v) => formatPorcentaje(v) }] : [])
    ],
    alertas: [
      { key: 'severidad', title: 'Severidad', exportValue: (v) => (v === 'alta' ? 'Alta' : 'Media') },
      { key: 'alumnoNombre', title: 'Alumno' },
      ...(conDivision ? [{ key: 'divisiones', title: 'División', exportValue: (v) => v || '-' }] : []),
      { key: 'tipo', title: 'Tipo', exportValue: (v) => TIPOS_ALERTA[v]?.label || v },
      { key: 'mensaje', title: 'Detalle' }
    ]
  };
  const exportRows = { cursos, alumnos, divisiones, alertas };

  return (
    <section className="ea-panel">
      <header className="ea-header">
        <div>
          <h2><i className="fas fa-chart-bar" aria-hidden="true" /> {titulo}</h2>
          {subtitulo && <p>{subtitulo}</p>}
        </div>
        <button type="button" className="ea-btn" onClick={load} disabled={loading}>
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} aria-hidden="true" />
          Actualizar
        </button>
      </header>

      {error && <p className="ea-error" role="alert">{error}</p>}

      {loading && !data ? (
        <div className="ea-empty"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Cargando estado académico...</div>
      ) : data && (
        <>
          <div className="ea-kpis">
            <Kpi icon="fa-chart-line" label="Promedio general" value={formatPromedio(data.totales.promedioGeneral)} tone="primary" />
            <Kpi icon="fa-user-graduate" label="Alumnos" value={data.totales.totalAlumnos} tone="neutral" />
            <Kpi icon="fa-check-circle" label="Aprobados" value={data.totales.alumnosAprobados} tone="success" />
            <Kpi icon="fa-exclamation-circle" label="En riesgo" value={data.totales.alumnosEnRiesgo} tone="danger" />
            <Kpi icon="fa-minus-circle" label="Sin notas" value={data.totales.alumnosSinNotas} tone="muted" />
            <Kpi icon="fa-layer-group" label="Cursos" value={data.totales.totalCursos} tone="neutral" />
            {conAsistencia && <Kpi icon="fa-user-clock" label="Asistencia promedio" value={formatPorcentaje(data.totales.porcentajeAsistencia)} tone="primary" />}
            <Kpi icon="fa-bell" label="Alumnos con alertas" value={data.totales.alumnosConAlertas} tone={data.totales.alumnosConAlertas > 0 ? 'danger' : 'muted'} />
          </div>

          <div className="ea-toolbar">
            <div className="ea-tabs" role="tablist">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={tabActiva === item.key}
                  className={tabActiva === item.key ? 'active' : ''}
                  onClick={() => setTab(item.key)}
                >
                  <i className={`fas ${item.icon}`} aria-hidden="true" />
                  {item.label}
                </button>
              ))}
            </div>
            <div className="ea-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={tabActiva === 'alumnos' || tabActiva === 'alertas' ? 'Buscar alumno, curso o división' : 'Buscar'}
                aria-label="Buscar"
              />
            </div>
            <TableExportActions
              columns={exportColumns[tabActiva]}
              rows={exportRows[tabActiva]}
              fileName={archivo}
              title={tituloExport}
              compact
              disabled={exportRows[tabActiva].length === 0}
            />
          </div>

          <div className="ea-table-wrap">
            {tabActiva === 'alertas' && (
              <table className="ea-table">
                <thead>
                  <tr>
                    <th>Alumno</th>
                    {conDivision && <th>División</th>}
                    <th>Alerta</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {alertas.map((alerta, index) => (
                    <tr key={`${alerta.idAlumno}-${alerta.tipo}-${index}`}>
                      <td>
                        <button type="button" className="ea-link" onClick={() => abrirAlumno(alerta.idAlumno)} title="Ver notas y asistencia del alumno">
                          {alerta.alumnoNombre}
                        </button>
                      </td>
                      {conDivision && <td>{alerta.divisiones || <span className="ea-muted">-</span>}</td>}
                      <td>
                        <span className={`ea-alerta ea-alerta-${alerta.severidad}`}>
                          <i className={`fas ${TIPOS_ALERTA[alerta.tipo]?.icon || 'fa-exclamation-circle'}`} aria-hidden="true" />
                          {TIPOS_ALERTA[alerta.tipo]?.label || alerta.tipo}
                        </span>
                      </td>
                      <td>{alerta.mensaje}</td>
                    </tr>
                  ))}
                  {alertas.length === 0 && <tr><td colSpan={4} className="ea-empty-row">Sin resultados.</td></tr>}
                </tbody>
              </table>
            )}

            {tabActiva === 'cursos' && (
              <table className="ea-table">
                <thead>
                  <tr>
                    <th>Curso</th>
                    {conDivision && <th>División</th>}
                    <th className="num">Alumnos</th>
                    <th className="num">Evaluaciones</th>
                    <th className="num">Promedio</th>
                    <th>Situación</th>
                    {conAsistencia && <th className="num">Asistencia</th>}
                    {onVerCurso && <th className="num" aria-label="Acciones" />}
                  </tr>
                </thead>
                <tbody>
                  {cursos.map((curso) => (
                    <tr key={curso.id}>
                      <td><strong>{curso.nombre}</strong></td>
                      {conDivision && <td>{curso.divisionNombre || <span className="ea-muted">Sin división</span>}</td>}
                      <td className="num">{curso.totalAlumnos}</td>
                      <td className="num">{curso.totalEvaluaciones}</td>
                      <td className={`num ea-prom${isNotaDesaprobada(curso.promedioGeneral) ? ' low' : ''}`}>{formatPromedio(curso.promedioGeneral)}</td>
                      <td><Reparto aprobados={curso.aprobados} enRiesgo={curso.enRiesgo} sinNotas={curso.sinNotas} /></td>
                      {conAsistencia && (
                        <td className="num">
                          <span className={`ea-asistencia${curso.porcentajeAsistencia !== null && curso.porcentajeAsistencia < ASISTENCIA_MINIMA ? ' low' : ''}`}>
                            <b>{formatPorcentaje(curso.porcentajeAsistencia)}</b>
                            {curso.alumnosBajaAsistencia > 0 && <small>{curso.alumnosBajaAsistencia} con baja asistencia</small>}
                          </span>
                        </td>
                      )}
                      {onVerCurso && (
                        <td className="num">
                          <button type="button" className="ea-btn ea-btn-sm" onClick={() => onVerCurso(curso)} title="Ver notas de cada alumno por evaluación">
                            <i className="fas fa-table" aria-hidden="true" />
                            Notas
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {cursos.length === 0 && <tr><td colSpan={8} className="ea-empty-row">{term ? 'Sin resultados.' : 'No hay cursos para mostrar.'}</td></tr>}
                </tbody>
              </table>
            )}

            {tabActiva === 'alumnos' && (
              <table className="ea-table">
                <thead>
                  <tr>
                    <th>Alumno</th>
                    {conDivision && <th>División</th>}
                    <th>Cursos</th>
                    <th className="num">Promedio</th>
                    <th>Estado</th>
                    {conAsistencia && <th className="num">Asistencia</th>}
                  </tr>
                </thead>
                <tbody>
                  {alumnos.map((alumno) => (
                    <tr key={alumno.id}>
                      <td>
                        <button type="button" className="ea-link" onClick={() => setAlumnoDetalle(alumno)} title="Ver el detalle por curso">
                          {alumno.nombre}
                        </button>
                        {alumno.email && <small className="ea-sub">{alumno.email}</small>}
                      </td>
                      {conDivision && <td>{alumno.divisiones || <span className="ea-muted">-</span>}</td>}
                      <td>
                        <div className="ea-cursos-chips">
                          {alumno.cursos.map((curso) => (
                            <span key={curso.idCurso} className={`ea-chip ea-chip-${getEstadoAcademico(curso.estado).tone}`} title={getEstadoAcademico(curso.estado).label}>
                              {curso.nombreCurso} <b>{formatPromedio(curso.promedio)}</b>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className={`num ea-prom${isNotaDesaprobada(alumno.promedioGeneral) ? ' low' : ''}`}>{formatPromedio(alumno.promedioGeneral)}</td>
                      <td>
                        <EstadoAcademicoChip estado={alumno.estado} showPromedio={false} />
                        {alumno.cursosEnRiesgo > 0 && <small className="ea-sub">{alumno.cursosEnRiesgo} {alumno.cursosEnRiesgo === 1 ? 'curso en riesgo' : 'cursos en riesgo'}</small>}
                      </td>
                      {conAsistencia && (
                        <td className="num">
                          <AsistenciaCelda asistencia={alumno.asistencia} />
                          {alumno.asistencia.ausenciasConsecutivas >= 3 && <small className="ea-sub ea-danger-text">{alumno.asistencia.ausenciasConsecutivas} ausencias seguidas</small>}
                        </td>
                      )}
                    </tr>
                  ))}
                  {alumnos.length === 0 && <tr><td colSpan={6} className="ea-empty-row">{term ? 'Sin resultados.' : 'No hay alumnos en estos cursos.'}</td></tr>}
                </tbody>
              </table>
            )}

            {tabActiva === 'divisiones' && (
              <table className="ea-table">
                <thead>
                  <tr>
                    <th>División</th>
                    <th className="num">Cursos</th>
                    <th className="num">Alumnos</th>
                    <th className="num">Promedio</th>
                    <th>Situación</th>
                    {conAsistencia && <th className="num">Asistencia</th>}
                  </tr>
                </thead>
                <tbody>
                  {divisiones.map((division) => (
                    <tr key={division.id}>
                      <td><strong>{division.nombreCompleto}</strong></td>
                      <td className="num">{division.totalCursos}</td>
                      <td className="num">{division.totalAlumnos}</td>
                      <td className={`num ea-prom${isNotaDesaprobada(division.promedioGeneral) ? ' low' : ''}`}>{formatPromedio(division.promedioGeneral)}</td>
                      <td><Reparto aprobados={division.aprobados} enRiesgo={division.enRiesgo} sinNotas={division.sinNotas} /></td>
                      {conAsistencia && (
                        <td className={`num ea-prom${division.porcentajeAsistencia !== null && division.porcentajeAsistencia < ASISTENCIA_MINIMA ? ' low' : ''}`}>
                          {formatPorcentaje(division.porcentajeAsistencia)}
                        </td>
                      )}
                    </tr>
                  ))}
                  {divisiones.length === 0 && <tr><td colSpan={5} className="ea-empty-row">Sin resultados.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
          <p className="ea-footnote">
            Aprueba con 6. El estado de cada curso sale del promedio de sus notas; el del alumno queda "En riesgo" si tiene al menos un curso en riesgo.
            {conAsistencia && ` Asistencia: presentes y tarde cuentan; por debajo del ${ASISTENCIA_MINIMA}% (con 3 tomas o más) o con 3 ausencias seguidas se genera una alerta.`}
          </p>
        </>
      )}

      {alumnoDetalle && (
        <div className="ea-overlay" onMouseDown={() => setAlumnoDetalle(null)}>
          <div className="ea-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div>
                <span className="ea-eyebrow">Estado académico del alumno</span>
                <h3>{alumnoDetalle.nombre}</h3>
              </div>
              <button type="button" className="ea-icon-btn" onClick={() => setAlumnoDetalle(null)} title="Cerrar">
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </header>
            <div className="ea-modal-body">
              {conDivision && alumnoDetalle.asistencia.total > 0 && (
                <div className={`ea-asistencia-box${esBajaAsistencia(alumnoDetalle.asistencia) ? ' low' : ''}`}>
                  <div><span>Asistencia</span><strong>{formatPorcentaje(alumnoDetalle.asistencia.porcentaje)}</strong></div>
                  <div><span>Presentes</span><strong>{alumnoDetalle.asistencia.presentes}</strong></div>
                  <div><span>Tarde</span><strong>{alumnoDetalle.asistencia.tardes}</strong></div>
                  <div><span>Faltas</span><strong>{alumnoDetalle.asistencia.ausentes}</strong></div>
                  <div><span>Justificadas</span><strong>{alumnoDetalle.asistencia.justificados}</strong></div>
                  {alumnoDetalle.asistencia.ausenciasConsecutivas > 0 && (
                    <div><span>Ausencias seguidas</span><strong>{alumnoDetalle.asistencia.ausenciasConsecutivas}</strong></div>
                  )}
                </div>
              )}
              {(data?.alertas || []).filter((a) => a.idAlumno === alumnoDetalle.id).map((alerta, index) => (
                <p key={`${alerta.tipo}-${index}`} className={`ea-alerta-linea ea-alerta-${alerta.severidad}`}>
                  <i className={`fas ${TIPOS_ALERTA[alerta.tipo]?.icon || 'fa-exclamation-circle'}`} aria-hidden="true" /> {alerta.mensaje}
                </p>
              ))}
              <div className="ea-alumno-resumen">
                <EstadoAcademicoChip estado={alumnoDetalle.estado} promedio={alumnoDetalle.promedioGeneral} />
                <TableExportActions
                  columns={[
                    { key: 'nombreCurso', title: 'Curso' },
                    ...(conDivision ? [{ key: 'divisionNombre', title: 'División', exportValue: (v) => v || '-' }] : []),
                    { key: 'promedio', title: 'Promedio', exportValue: (v) => formatPromedio(v) },
                    { key: 'cantidadNotas', title: 'Notas' },
                    { key: 'cantidadAusentes', title: 'Ausente en evaluaciones' },
                    { key: 'estado', title: 'Estado', exportValue: (v) => getEstadoAcademico(v).label }
                  ]}
                  rows={alumnoDetalle.cursos}
                  fileName={`estado-academico-${alumnoDetalle.nombre}`}
                  title={`Estado académico - ${alumnoDetalle.nombre}`}
                  compact
                />
              </div>
              <table className="ea-table">
                <thead>
                  <tr>
                    <th>Curso</th>
                    {conDivision && <th>División</th>}
                    <th className="num">Promedio</th>
                    <th className="num">Notas</th>
                    <th className="num" title="Evaluaciones en las que estuvo ausente">Aus. en evaluaciones</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {alumnoDetalle.cursos.map((curso) => (
                    <tr key={curso.idCurso}>
                      <td><strong>{curso.nombreCurso}</strong></td>
                      {conDivision && <td>{curso.divisionNombre || '-'}</td>}
                      <td className={`num ea-prom${isNotaDesaprobada(curso.promedio) ? ' low' : ''}`}>{formatPromedio(curso.promedio)}</td>
                      <td className="num">{curso.cantidadNotas}</td>
                      <td className="num">{curso.cantidadAusentes}</td>
                      <td><EstadoAcademicoChip estado={curso.estado} showPromedio={false} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default EstadoAcademicoPanel;
